import React, { useState, useEffect, useRef, useCallback } from 'react'
import useProcessingStore from '../stores/processingStore'
import { Database, Activity, Lock, ChevronLeft, Trash2, Users, Sparkles, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button, Card, Input, Label } from '../components/ui'
import { cn } from '../lib/utils'
import AnimatedGradientBackground from '../components/AnimatedGradientBackground'
import { convexClient } from '../convexClient'
import { api as convexApi } from '../lib/convexHelpers'

import AdminSongsTab from '../components/admin/AdminSongsTab'
import AdminProcessingTab from '../components/admin/AdminProcessingTab'
import AdminIntegrityTab from '../components/admin/AdminIntegrityTab'
import AdminRoomsTab from '../components/admin/AdminRoomsTab'
import AdminWrappedTab from '../components/admin/AdminWrappedTab'
import { SystemStatus } from '../components/admin/AdminSettings'

const toIsoDate = (value) => {
  if (!value) return null
  if (typeof value === 'number') return new Date(value).toISOString()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

const mapLyricsForAdmin = (lyrics) => {
  if (!Array.isArray(lyrics)) return []
  return lyrics.map((line) => ({
    text: line?.text || '',
    start_time: line?.start_time ?? line?.startTime ?? 0,
    end_time: line?.end_time ?? line?.endTime ?? 0,
  }))
}

const mapSongForAdmin = (song) => ({
  id: song?.id || song?._id || '',
  title: song?.title || 'Untitled',
  artist: song?.artist || 'Unknown Artist',
  duration: song?.duration || null,
  youtubeId: song?.youtubeId || null,
  lyrics: mapLyricsForAdmin(song?.lyrics),
  wordLevelTranscription: song?.wordLevelTranscription || null,
  originalKey: song?.originalKey || null,
  vocalRangeLow: song?.vocalRangeLow || null,
  vocalRangeHigh: song?.vocalRangeHigh || null,
  isProcessed: song?.isProcessed ?? false,
  processingError: song?.processingError || null,
  geminiRawResponse: song?.geminiRawResponse || null,
  geminiPrompt: song?.geminiPrompt || null,
  createdAt: toIsoDate(song?._creationTime),
})

const mapRoomForAdmin = (room) => ({
  id: room?.id || room?._id || '',
  code: room?.code || '',
  isActive: room?.isActive ?? false,
  songCount: room?.queueCount ?? 0,
  lastActivity: toIsoDate(room?.lastActivity ?? room?._creationTime),
  createdAt: toIsoDate(room?._creationTime),
})

const mapQueueItemForAdmin = (item) => ({
  id: item?.id || item?._id || '',
  songId: item?.songId || '',
  position: item?.position ?? 0,
  songTitle: item?.song?.title || 'Unknown',
  songArtist: item?.song?.artist || 'Unknown',
  youtubeId: item?.song?.youtubeId || null,
  addedBy: item?.addedBy || 'anonymous',
  createdAt: toIsoDate(item?.createdAt || item?._creationTime),
  status: item?.status || 'queued',
})

const AdminPage = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [password, setPassword] = useState('')

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/check', { credentials: 'include' })
        const data = response.ok ? await response.json() : null
        if (data?.authenticated === true || data?.status === 'authenticated') {
          setIsAuthenticated(true)
        } else {
          setIsAuthenticated(false)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
      } finally {
        setIsCheckingAuth(false)
      }
    }
    checkAuth()
  }, [])

  // Processing store
  const {
    processingJobs,
    jobsFilter,
    jobsLoading,
    retryProcessingJob,
    loadProcessingJobs,
    setJobsFilter,
  } = useProcessingStore()

  // ALL Local state hooks must be called before any conditional returns
  const [activeTab, setActiveTab] = useState('database') // 'database' or 'processing'

  // Songs Database Tab State
  const [searchQuery, setSearchQuery] = useState('')
  const [songsPage, setSongsPage] = useState(1)
  const [songsPerPage, setSongsPerPage] = useState(25)
  const [allSongs, setAllSongs] = useState([])
  const [totalSongs, setTotalSongs] = useState(0)
  const [loadingSongs, setLoadingSongs] = useState(false)
  const [selectedSong, setSelectedSong] = useState(null)
  const [showRawData, setShowRawData] = useState(false)
  const [showLyricsModal, setShowLyricsModal] = useState(false)

  // Processing Jobs Tab State
  const [jobsPage, setJobsPage] = useState(1)
  const [jobsPerPage, setJobsPerPage] = useState(25)

  const [integrityData, setIntegrityData] = useState(null)
  const [integrityLoading, setIntegrityLoading] = useState(false)

  // Rooms Tab State
  const [rooms, setRooms] = useState([])
  const [loadingRooms, setLoadingRooms] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState(null)
  const [roomQueue, setRoomQueue] = useState([])
  const [loadingQueue, setLoadingQueue] = useState(false)

  // Delete functionality state
  const [deletingSongId, setDeletingSongId] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [songToDelete, setSongToDelete] = useState(null)
  const previousSearchRef = useRef(searchQuery)

  // Fetch all songs with pagination
  const fetchSongs = useCallback(async (page = 1, perPage = 25, query = '') => {
    setLoadingSongs(true)
    try {
      const skip = (page - 1) * perPage

      if (query.trim()) {
        const data = await convexClient.query(convexApi.songs.search, { query, limit: perPage })
        const normalized = (data || []).map(mapSongForAdmin)
        setAllSongs(normalized)
        setTotalSongs(normalized.length)
      } else {
        const data = await convexClient.query(convexApi.songs.list, {
          limit: perPage,
          offset: skip,
          processedOnly: true,
        })
        const normalized = (data || []).map(mapSongForAdmin)
        setAllSongs(normalized)
        setTotalSongs(normalized.length === perPage ? (page * perPage) + 1 : ((page - 1) * perPage) + normalized.length)
      }
    } catch (error) {
      console.error('Failed to fetch songs:', error)
      setAllSongs([])
      setTotalSongs(0)
      toast.error(`Failed to load songs: ${error.message}`)
    } finally {
      setLoadingSongs(false)
    }
  }, [])

  // Fetch all rooms
  const fetchRooms = useCallback(async () => {
    setLoadingRooms(true)
    try {
      const data = await convexClient.query(convexApi.rooms.listAll, { limit: 500 })
      setRooms((data || []).map(mapRoomForAdmin))
    } catch (error) {
      console.error('Failed to fetch rooms:', error)
      toast.error('Failed to load rooms')
    } finally {
      setLoadingRooms(false)
    }
  }, [])

  const loadIntegrityIssues = useCallback(async () => {
    setIntegrityLoading(true)
    try {
      const data = await convexClient.query(convexApi.songs.listIntegrityIssues, {
        processedOnly: true,
      })
      setIntegrityData(data)
    } catch (error) {
      console.error('Failed to scan song integrity:', error)
      toast.error(`Integrity scan failed: ${error.message}`)
      setIntegrityData(null)
    } finally {
      setIntegrityLoading(false)
    }
  }, [])

  // Inspect specific room queue
  const inspectRoom = useCallback(async (room) => {
    setSelectedRoom(room)
    if (!room) {
      setRoomQueue([])
      return
    }

    setLoadingQueue(true)
    try {
      const data = await convexClient.query(convexApi.queue.getQueue, { roomId: room.id })
      setRoomQueue((data || []).map(mapQueueItemForAdmin))
    } catch (error) {
      console.error('Failed to fetch room queue:', error)
      toast.error('Failed to load room queue')
    } finally {
      setLoadingQueue(false)
    }
  }, [])

  // Fetch song details with raw data
  const fetchSongDetails = useCallback(async (songId, options = {}) => {
    const { switchToDatabaseTab = false } = options
    try {
      const data = await convexClient.query(convexApi.songs.get, { id: songId })
      if (!data) throw new Error('Song not found')
      setSelectedSong(mapSongForAdmin(data))
      setShowRawData(true)
      if (switchToDatabaseTab) {
        setActiveTab('database')
      }
    } catch (error) {
      console.error('Failed to fetch song details:', error)
      toast.error('Failed to load song details')
    }
  }, [])

  const handleViewLyrics = useCallback(async (songId) => {
    try {
      const data = await convexClient.query(convexApi.songs.get, { id: songId })
      if (!data) throw new Error('Song not found')
      setSelectedSong(mapSongForAdmin(data))
      setShowLyricsModal(true)
    } catch (error) {
      console.error('Failed to fetch lyrics:', error)
      toast.error('Failed to load lyrics')
    }
  }, [])

  // Delete song functionality
  const handleDeleteClick = (song) => {
    setSongToDelete(song)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!songToDelete) return
    setDeletingSongId(songToDelete.id)
    setShowDeleteConfirm(false)
    try {
      const response = await fetch(`/api/songs/${songToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      if (response.ok) {
        const result = await response.json()
        toast.success(result.message || 'Song deleted successfully')
        fetchSongs(songsPage, songsPerPage, searchQuery)
      } else {
        const errorData = await response.json()
        toast.error(errorData.detail?.message || errorData.detail || 'Failed to delete song')
      }
    } catch (error) {
      console.error('Failed to delete song:', error)
      toast.error('Failed to delete song: Network error')
    } finally {
      setDeletingSongId(null)
      setSongToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
    setSongToDelete(null)
  }

  // Load initial data
  useEffect(() => {
    const queryChanged = previousSearchRef.current !== searchQuery
    previousSearchRef.current = searchQuery

    if (queryChanged && songsPage !== 1) {
      setSongsPage(1)
      return
    }

    const timer = setTimeout(() => {
      fetchSongs(songsPage, songsPerPage, searchQuery)
    }, queryChanged ? 300 : 0)

    return () => clearTimeout(timer)
  }, [fetchSongs, songsPage, songsPerPage, searchQuery])

  useEffect(() => {
    loadProcessingJobs()
  }, [loadProcessingJobs])

  useEffect(() => {
    fetchRooms()
  }, [fetchRooms])

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'integrity') return
    loadIntegrityIssues()
  }, [isAuthenticated, activeTab, loadIntegrityIssues])

  // Defensive reset: other routes may leave body/html overflow locked.
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow

    if (previousBodyOverflow === 'hidden') {
      document.body.style.overflow = 'auto'
    }
    if (previousHtmlOverflow === 'hidden') {
      document.documentElement.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [])

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
        credentials: 'include'
      })
      if (response.ok) {
        setIsAuthenticated(true)
        toast.success('Access granted')
      } else {
        const errorData = await response.json()
        toast.error(errorData.detail || 'Incorrect password')
        setPassword('')
      }
    } catch (error) {
      console.error('Login failed:', error)
      toast.error('Login failed. Please try again.')
    }
  }

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
      if (response.ok) {
        setIsAuthenticated(false)
        toast.success('Logged out successfully')
      }
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
        <AnimatedGradientBackground brightness={30} className="absolute inset-0 z-0" />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 relative z-10"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
        <AnimatedGradientBackground brightness={30} className="absolute inset-0 z-0" />
        <Card className="w-full max-w-md space-y-6 border-white/20 bg-white/10 backdrop-blur-xl p-8 shadow-2xl relative z-10">
          <div className="text-center">
            <div className="bg-primary-500/20 backdrop-blur-sm rounded-2xl w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-primary-500/30">
              <Lock className="w-10 h-10 text-primary-400" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">Admin Access</h2>
            <p className="text-slate-400">Please enter the password to continue</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div className="space-y-2 text-left">
              <Label htmlFor="admin-password" id="admin-password-label" className="text-slate-300 ml-1">Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                autoFocus
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12"
              />
            </div>
            <Button type="submit" className="w-full h-12 text-base font-semibold shadow-lg shadow-primary-500/20" variant="primary" size="lg">Unlock Terminal</Button>
          </form>
          <div className="text-center pt-2">
            <a href="/" className="text-slate-400 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2">
              <ChevronLeft className="w-4 h-4" /> Back to App
            </a>
          </div>
        </Card>
      </div>
    )
  }

  // Processing jobs logic
  const getFilteredJobs = () => {
    const jobs = Array.from(processingJobs.values())
    switch (jobsFilter) {
      case 'active': return jobs.filter(job => job.status === 'pending' || job.status === 'processing')
      case 'completed': return jobs.filter(job => job.status === 'completed')
      case 'failed': return jobs.filter(job => job.status === 'failed')
      default: return jobs
    }
  }

  const filteredJobs = getFilteredJobs()
  const paginatedJobs = filteredJobs.slice((jobsPage - 1) * jobsPerPage, jobsPage * jobsPerPage)
  const totalJobPages = Math.ceil(filteredJobs.length / jobsPerPage)
  const totalSongPages = Math.ceil(totalSongs / songsPerPage)

  const stats = {
    total: Array.from(processingJobs.values()).length,
    pending: Array.from(processingJobs.values()).filter(j => j.status === 'pending').length,
    processing: Array.from(processingJobs.values()).filter(j => j.status === 'processing').length,
    completed: Array.from(processingJobs.values()).filter(j => j.status === 'completed').length,
    failed: Array.from(processingJobs.values()).filter(j => j.status === 'failed').length
  }

  const formatDate = (dateString) => new Date(dateString).toLocaleString()
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }
  const extractVideoId = (url) => {
    try {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)
      return match ? match[1] : 'Unknown'
    } catch { return 'Unknown' }
  }

  return (
    <div className="h-[100dvh] overflow-y-auto bg-slate-950 text-white relative">
      <AnimatedGradientBackground brightness={20} className="fixed inset-0 z-0" />
      <div className="relative z-10">
        <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-primary-500 p-2 rounded-xl shadow-lg shadow-primary-500/20">
                  <Database className="h-6 w-6 text-white" />
                </div>
                <h1 className="text-2xl font-black tracking-tighter text-white uppercase">Karaoke <span className="text-primary-400">Admin</span></h1>
              </div>
              <div className="flex items-center space-x-4">
                <button onClick={handleLogout} className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-full text-sm font-bold text-red-400 transition-all flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Logout
                </button>
                <a href="/" className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-sm font-bold transition-all flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Back to App
                </a>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
          <SystemStatus stats={stats} />

          <div className="flex flex-col space-y-6">
            <div className="flex space-x-1 p-1 bg-black/20 backdrop-blur-md border border-white/10 rounded-2xl w-fit">
              <button
                onClick={() => setActiveTab('database')}
                className={cn(
                  "flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300",
                  activeTab === 'database' ? "bg-white/10 text-white border border-white/10 shadow-xl" : "text-slate-500 hover:text-slate-300"
                )}
              >
                <Database className="h-4 w-4" />
                <span>Database</span>
              </button>
              <button
                onClick={() => setActiveTab('processing')}
                className={cn(
                  "flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300",
                  activeTab === 'processing' ? "bg-white/10 text-white border border-white/10 shadow-xl" : "text-slate-500 hover:text-slate-300"
                )}
              >
                <Activity className="h-4 w-4" />
                <span>Processing</span>
                {(stats.pending + stats.processing) > 0 && (
                  <span className="ml-2 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] text-white animate-pulse">
                    {stats.pending + stats.processing}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('integrity')}
                className={cn(
                  "flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300",
                  activeTab === 'integrity' ? "bg-white/10 text-white border border-white/10 shadow-xl" : "text-slate-500 hover:text-slate-300"
                )}
              >
                <AlertTriangle className="h-4 w-4" />
                <span>Data quality</span>
                {(integrityData?.issueSongCount ?? 0) > 0 && (
                  <span className="ml-2 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] text-slate-950 font-black">
                    {integrityData.issueSongCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('rooms')}
                className={cn(
                  "flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300",
                  activeTab === 'rooms' ? "bg-white/10 text-white border border-white/10 shadow-xl" : "text-slate-500 hover:text-slate-300"
                )}
              >
                <Users className="h-4 w-4" />
                <span>Rooms</span>
              </button>

              <button
                onClick={() => setActiveTab('wrapped')}
                className={cn(
                  "flex items-center space-x-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all duration-300",
                  activeTab === 'wrapped' ? "bg-white/10 text-white border border-white/10 shadow-xl" : "text-slate-500 hover:text-slate-300"
                )}
              >
                <Sparkles className="h-4 w-4" />
                <span>Wrapped</span>
              </button>
            </div>

            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {activeTab === 'database' && (
                <AdminSongsTab
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  allSongs={allSongs}
                  loadingSongs={loadingSongs}
                  songsPage={songsPage}
                  setSongsPage={setSongsPage}
                  songsPerPage={songsPerPage}
                  setSongsPerPage={setSongsPerPage}
                  totalSongPages={totalSongPages}
                  fetchSongs={fetchSongs}
                  fetchSongDetails={fetchSongDetails}
                  handleDeleteClick={handleDeleteClick}
                  deletingSongId={deletingSongId}
                  showRawData={showRawData}
                  setShowRawData={setShowRawData}
                  showLyricsModal={showLyricsModal}
                  setShowLyricsModal={setShowLyricsModal}
                  selectedSong={selectedSong}
                  setSelectedSong={setSelectedSong}
                  handleViewLyrics={handleViewLyrics}
                  formatDuration={formatDuration}
                  formatDate={formatDate}
                />
              )}
              {activeTab === 'processing' && (
                <AdminProcessingTab
                  stats={stats}
                  jobsFilter={jobsFilter}
                  setJobsFilter={setJobsFilter}
                  jobsLoading={jobsLoading}
                  loadProcessingJobs={loadProcessingJobs}
                  paginatedJobs={paginatedJobs}
                  filteredJobs={filteredJobs}
                  jobsPage={jobsPage}
                  setJobsPage={setJobsPage}
                  jobsPerPage={jobsPerPage}
                  setJobsPerPage={setJobsPerPage}
                  totalJobPages={totalJobPages}
                  retryProcessingJob={retryProcessingJob}
                  formatDate={formatDate}
                  extractVideoId={extractVideoId}
                />
              )}
              {activeTab === 'integrity' && (
                <AdminIntegrityTab
                  integrityLoading={integrityLoading}
                  integrityData={integrityData}
                  loadIntegrityIssues={loadIntegrityIssues}
                  fetchSongDetails={(songId) => fetchSongDetails(songId, { switchToDatabaseTab: true })}
                />
              )}
              {activeTab === 'rooms' && (
                <AdminRoomsTab
                  rooms={rooms}
                  loadingRooms={loadingRooms}
                  fetchRooms={fetchRooms}
                  inspectRoom={inspectRoom}
                  selectedRoom={selectedRoom}
                  roomQueue={roomQueue}
                  loadingQueue={loadingQueue}
                  formatDate={formatDate}
                />
              )}

              {activeTab === 'wrapped' && (
                <AdminWrappedTab
                  rooms={rooms}
                  loadingRooms={loadingRooms}
                  fetchRooms={fetchRooms}
                />
              )}
            </div>
          </div>
        </main>
      </div>

      {showDeleteConfirm && songToDelete && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <Card className="max-w-md w-full border-white/10 bg-slate-900 shadow-2xl p-8 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="bg-red-500/20 rounded-2xl p-4 mb-4 border border-red-500/20"><Trash2 className="w-10 h-10 text-red-500" /></div>
              <h3 className="text-2xl font-black text-white tracking-tighter uppercase">Delete Song?</h3>
              <p className="text-slate-400 font-medium">This operation is destructive and permanent.</p>
            </div>
            <div className="bg-black/20 rounded-2xl p-5 mb-8 border border-white/5">
              <p className="text-white font-bold mb-4">Confirm deletion of <span className="text-primary-400 underline decoration-primary-500/30 underline-offset-4">"{songToDelete.title}"</span>?</p>
              <ul className="text-xs font-medium text-slate-500 space-y-2">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-red-500" /> Database records</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-red-500" /> All audio stems & files</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-red-500" /> Room queue entries</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-red-500" /> Processing history</li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Button type="button" variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10 h-12 font-bold" onClick={handleDeleteCancel}>No, Keep it</Button>
              <Button type="button" className="bg-red-600 hover:bg-red-700 text-white shadow-xl shadow-red-600/20 h-12 font-bold" onClick={handleDeleteConfirm}>Yes, Delete</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export default AdminPage
